import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Loader2, UserCircle2, CheckCircle2, XCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

/**
 * Landing page for BOTH social providers.
 *  - Google (Emergent Auth):  #session_id=...   → POST /auth/google/session
 *  - Facebook (backend 302):  #st=<jwt>&nu=0    → direct login
 *                             #pending=<tok>&suggested=...&nu=1 → username step
 * New users MUST choose a username before the account is created.
 */
export default function SocialCompletePage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh } = useAuth();
  const processed = useRef(false);

  const [phase, setPhase] = useState('processing'); // processing | username | error
  const [pendingToken, setPendingToken] = useState('');
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Live availability: idle | checking | available | taken | invalid
  const [availability, setAvailability] = useState('idle');

  const isValidFormat = (u) =>
    u.length >= 3 && /^[a-z0-9._]+$/.test(u) && u.replace(/[._]/g, '').length > 0;

  useEffect(() => {
    if (phase !== 'username') return;
    const u = username.trim();
    if (!u) { setAvailability('idle'); return; }
    if (!isValidFormat(u)) { setAvailability('invalid'); return; }
    setAvailability('checking');
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/check/${encodeURIComponent(u)}`);
        setAvailability(data.available ? 'available' : 'taken');
      } catch {
        setAvailability('idle');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [username, phase]);

  const finalize = async (token) => {
    localStorage.setItem('ig_token', token);
    await refresh();
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const sessionId = params.get('session_id');
    const st = params.get('st');
    const pending = params.get('pending');
    const err = params.get('error');
    // Strip the fragment so tokens never linger in history.
    window.history.replaceState(null, '', window.location.pathname);

    (async () => {
      try {
        if (err) {
          setErrorMsg(t('social.error_generic'));
          setPhase('error');
          return;
        }
        if (st) {
          await finalize(st);
          return;
        }
        if (pending) {
          setPendingToken(pending);
          setUsername(params.get('suggested') || '');
          setPhase('username');
          return;
        }
        if (sessionId) {
          const { data } = await api.post('/auth/google/session', { session_id: sessionId });
          if (data.needs_username) {
            setPendingToken(data.pending_token);
            setUsername(data.suggested_username || '');
            setPhase('username');
          } else {
            await finalize(data.token);
          }
          return;
        }
        navigate('/login', { replace: true });
      } catch (e) {
        setErrorMsg(String(e?.response?.data?.detail || '').split('|').pop() || t('social.error_generic'));
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/social/complete', {
        pending_token: pendingToken,
        username: username.trim(),
        preferred_language: i18n.language?.startsWith('en') ? 'en' : 'it',
      });
      toast({ title: t('social.welcome_toast') });
      await finalize(data.token);
    } catch (e2) {
      toast({
        title: t('social.username_error'),
        description: String(e2?.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4" data-testid="social-complete-page">
      <div className="w-full max-w-sm border border-border bg-card px-8 py-10 text-center">
        <h1 className="mb-6 text-4xl nymia-logo">Nymia</h1>

        {phase === 'processing' && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="social-processing">
            <Loader2 className="h-6 w-6 animate-spin gold-text" />
            <p className="text-sm text-muted-foreground">{t('social.processing')}</p>
          </div>
        )}

        {phase === 'username' && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 text-left" data-testid="social-username-form">
            <div className="mb-1 flex flex-col items-center gap-2 text-center">
              <UserCircle2 className="h-8 w-8 gold-text" strokeWidth={1.5} />
              <h2 className="text-base font-semibold">{t('social.choose_username_title')}</h2>
              <p className="text-xs text-muted-foreground">{t('social.choose_username_hint')}</p>
            </div>
            <div className="relative">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                placeholder={t('social.username_placeholder')}
                className={`h-9 rounded-sm bg-background pr-9 text-xs ${
                  availability === 'available'
                    ? 'border-emerald-500/60 focus-visible:ring-emerald-500/30'
                    : availability === 'taken' || availability === 'invalid'
                      ? 'border-red-500/70 focus-visible:ring-red-500/40'
                      : 'border-border'
                }`}
                autoFocus
                data-testid="social-username-input"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                {availability === 'checking' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" data-testid="social-username-checking" />}
                {availability === 'available' && <CheckCircle2 className="h-4 w-4 text-emerald-500" data-testid="social-username-available" />}
                {(availability === 'taken' || availability === 'invalid') && <XCircle className="h-4 w-4 text-red-500" data-testid="social-username-unavailable" />}
              </span>
            </div>
            {availability === 'available' && (
              <p className="px-1 text-[11px] text-emerald-400" data-testid="social-username-available-hint">{t('social.username_available')}</p>
            )}
            {availability === 'taken' && (
              <p className="px-1 text-[11px] text-red-400" data-testid="social-username-taken-hint">{t('social.username_taken')}</p>
            )}
            {availability === 'invalid' && username.trim().length > 0 && (
              <p className="px-1 text-[11px] text-red-400" data-testid="social-username-invalid-hint">{t('social.username_invalid')}</p>
            )}
            <Button
              type="submit"
              disabled={availability !== 'available' || submitting}
              className="h-9 gold-gradient text-sm font-semibold text-black disabled:opacity-60"
              data-testid="social-username-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('social.complete_btn')}
            </Button>
          </form>
        )}

        {phase === 'error' && (
          <div className="flex flex-col items-center gap-4 py-4" data-testid="social-error">
            <p className="text-sm text-red-400">{errorMsg}</p>
            <Button variant="secondary" onClick={() => navigate('/login', { replace: true })} data-testid="social-back-login">
              {t('social.back_to_login')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
